/**
 * Filter Defaults
 *
 * Centralized default values for filter state, organized by tenant and date context.
 * This file consolidates hardcoded dates that were previously scattered across
 * FilterBar.tsx, BasicDatePicker.tsx, and filterSlice.ts.
 */

import type { DateRange } from 'react-day-picker'

import type { TenantKey } from '@/config/types'
import {
  getBranchesByTenant,
  getDefaultBranchType as getDefaultBranchTypeUtil,
} from '@/utils'
import type { DateType } from '@/utils/date'

import type { BranchType, DateContextType } from './filterSlice'

// ============================================================================
// MONTHLY DATE DEFAULTS
// ============================================================================

/**
 * Get default date range for monthly context pages.
 * Currently same for all tenants: July 1-26, 2024
 *
 * Used by: Sales, Labor, Speed of Service, Loyalty, Dashboard, Sales Lens
 */
export const getDefaultMonthlyDate = (_tenant: TenantKey): DateRange => ({
  from: new Date(2024, 6, 1), // July 1, 2024
  to: new Date(2024, 6, 26), // July 26, 2024
})

/**
 * Get default date filter type for monthly context pages.
 */
export const getDefaultMonthlyDateFilterType = (): DateType => 'month-to-date'

// ============================================================================
// QUARTERLY DATE DEFAULTS
// ============================================================================

/**
 * Get default date range for quarterly context pages.
 * Tenant-specific:
 * - blankstreet: Q2 2025 (April 1 - June 30, 2025)
 * - dev: Q1 2024 (January 1 - March 31, 2024)
 *
 * Used by: Spend, Profit & Loss, Spend Lens, PnL Lens
 */
export const getDefaultQuarterlyDate = (tenant: TenantKey): DateRange => {
  if (tenant === 'blankstreet') {
    return {
      from: new Date(2025, 3, 1), // April 1, 2025 (Q2)
      to: new Date(2025, 5, 30), // June 30, 2025
    }
  }
  // dev tenant - Q1 2024
  return {
    from: new Date(2024, 0, 1), // January 1, 2024 (Q1)
    to: new Date(2024, 2, 31), // March 31, 2024
  }
}

/**
 * Get default date filter type for quarterly context pages.
 */
export const getDefaultQuarterlyDateFilterType = (): DateType => 'quarterly'

// ============================================================================
// BRANCH DEFAULTS
// ============================================================================

/**
 * Get default branch value for a tenant.
 * Re-exports from utils to centralize all defaults in one place.
 */
export const getDefaultBranch = (tenant: TenantKey): string => {
  return getBranchesByTenant(tenant)
}

/**
 * Get default branch type for a tenant.
 * - blankstreet: 'all' stores
 * - dev: 'same' stores
 */
export const getDefaultBranchType = (tenant: TenantKey): BranchType => {
  return getDefaultBranchTypeUtil(tenant)
}

// ============================================================================
// CONTEXT-AWARE DEFAULTS
// ============================================================================

/**
 * Get default date range based on context and tenant.
 */
export const getDefaultDateForContext = (
  context: DateContextType,
  tenant: TenantKey
): DateRange => {
  return context === 'monthly'
    ? getDefaultMonthlyDate(tenant)
    : getDefaultQuarterlyDate(tenant)
}

/**
 * Get default date filter type based on context.
 */
export const getDefaultDateFilterTypeForContext = (
  context: DateContextType
): DateType => {
  return context === 'monthly'
    ? getDefaultMonthlyDateFilterType()
    : getDefaultQuarterlyDateFilterType()
}
